import type React from "react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaRegEdit } from "react-icons/fa";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import Spinner from "src/common/components/Loader/Spinner";
import type { menuItems } from "src/common/components/Icon/ThreeDotsDropdownMenu";
import ThreeDotsDropdownMenu from "src/common/components/Icon/ThreeDotsDropdownMenu";
import fromTimestampToPrettyFrenchDate from "src/common/util/fromTimestampToPrettyFrenchDate";
import {
  getRagConversations,
  getRagConversationsPagination,
  getActiveRagConversationId,
  fetchMoreConversationsThunk,
  deleteConversationThunk,
} from "../state/ragSlice";

interface RagConversationLoaderProps {
  onConversationSelect: (conversationId: number) => void;
  onNewConversation: () => void;
}

/**
 * Wixarpedia's conversation history sidebar.
 *
 * A deliberate visual clone of GptConversationLoader (Scenar IA's sidebar): same
 * "Nouvelle requête" button, same row treatment and active state, same
 * infinite-scroll trigger, same French pretty-date fallback for untitled rows.
 * The two panels sit in the identical 240px slot of the identical three-column
 * shell, so any divergence here would read as a bug.
 *
 * Renaming is intentionally absent — Wixarpedia titles itself from the opening
 * question, and rename was deferred. That's why this uses a delete-only menu
 * rather than reusing GptConversationThreeDotButton (which is also bound to
 * chatbotSlice, a different feature's store).
 */
const RagConversationLoader: React.FC<RagConversationLoaderProps> = ({
  onConversationSelect,
  onNewConversation,
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const conversations = useAppSelector(getRagConversations);
  const pagination = useAppSelector(getRagConversationsPagination);
  const activeConversationId = useAppSelector(getActiveRagConversationId);

  const [deletingId, setDeletingId] = useState<number | null>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const el = scrollableRef.current;
    if (!el || !pagination.hasMore || pagination.isLoadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      void dispatch(fetchMoreConversationsThunk({ start: conversations.length }));
    }
  };

  const handleDelete = async (conversationId: number) => {
    if (deletingId !== null) return;
    setDeletingId(conversationId);
    try {
      await dispatch(deleteConversationThunk(conversationId));
    } finally {
      setDeletingId(null);
    }
  };

  // Same scroll nudge as the Scenar IA sidebar: the last row's dropdown would
  // otherwise open past the bottom of a scrolled list.
  const handleThreeDotMenuOpen = (conversationId: number) => {
    const isLastItem = conversations[conversations.length - 1]?.id === conversationId;
    if (!isLastItem || !scrollableRef.current) return;
    const container = scrollableRef.current;
    if (container.scrollHeight > container.clientHeight) {
      container.scrollTop = container.scrollHeight;
    }
  };

  return (
    <div className="w-full max-h-[80vh] h-[80vh] p-1 bg-gray-100">
      <button
        className="w-full bg-white p-2 mb-1 rounded-lg shadow hover:bg-green-500 hover:text-white border transition-colors"
        onClick={onNewConversation}
      >
        <div className="flex items-center justify-center font-bold p-2">
          <FaRegEdit className="mr-2" />
          {t("pages.chat.new")}
        </div>
      </button>

      <div
        ref={scrollableRef}
        onScroll={handleScroll}
        className="space-y-2 overflow-hidden overflow-y-auto h-[93%]"
      >
        {conversations.map((conversation, index) => {
          // Titles come from the opening question; the date fallback covers rows
          // whose question was empty or lost.
          const name =
            conversation.title ?? fromTimestampToPrettyFrenchDate(conversation.created_at);
          const isActive = activeConversationId === conversation.id;

          const menu: menuItems = [
            {
              menuItemText: t("general.delete"),
              itemId: t("general.delete"),
              icon: "TrashIcon",
              disabled: deletingId === conversation.id,
            },
          ];

          return (
            <div key={conversation.id} className="relative group">
              <button
                className={`w-full text-sm p-2 rounded-lg shadow transition-colors pr-10 text-left h-[44px] flex items-center ${
                  isActive ? "bg-green-600 text-white" : "bg-white hover:bg-green-100"
                }`}
                onClick={() => onConversationSelect(conversation.id)}
              >
                <span className="line-clamp-2 leading-tight">{name}</span>
              </button>
              <div className="absolute top-1 right-1 h-full flex items-center">
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <ThreeDotsDropdownMenu
                    className="h-4 w-6"
                    menuItems={menu}
                    handleClick={(item) => {
                      if (item.menuItemText === t("general.delete")) {
                        void handleDelete(conversation.id);
                      }
                    }}
                    onMenuOpen={() => handleThreeDotMenuOpen(conversation.id)}
                    menuPosition={index <= 3 ? "below" : "above"}
                  />
                </div>
              </div>
            </div>
          );
        })}

        {pagination.isLoadingMore && (
          <div className="flex justify-center py-2">
            <Spinner relative style={{ width: 24, height: 24 }} />
          </div>
        )}
        {pagination.hasMore && <div className="h-1" />}
      </div>
    </div>
  );
};

export default RagConversationLoader;
